/**
 * "Pick up where you left off" — the few things the person touched last,
 * newest first (ADR-0070): release validation runs they own or are named on,
 * their risk assessments, their support and Explore chats, and — for
 * Developers and Administrators — the pipelines that ran most recently
 * (`recentItems.tsx`). Read once when Home opens.
 */

import { Divider, Group, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { Fragment, useEffect, useRef, useState } from "react";
import { notificationWhen } from "@/app/notificationMeta";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { HomeRow } from "./HomeRow";
import { loadRecent, type Person, type RecentItem } from "./recentItems";

export function RecentCard({ me }: { me: Person | undefined }) {
  const getToken = useAccessToken();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [items, setItems] = useState<RecentItem[] | null>(null);
  const userId = me?.user_id;
  const meRef = useRef(me);
  meRef.current = me;

  // Read once per person, when Home opens; the token getter and the profile are
  // read through refs (an unstable dependency here would reload forever).
  useEffect(() => {
    const person = meRef.current;
    if (!userId || !person) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await loadRecent(await getTokenRef.current(), person);
        if (!cancelled) setItems(rows);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <Paper withBorder radius="md" p="lg">
      <Title order={4} mb="xs">
        Pick up where you left off
      </Title>
      {items === null ? (
        <Group justify="center" p="md">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" py="sm">
          Nothing yet. Your recent runs, assessments and chats will show here.
        </Text>
      ) : (
        <Stack gap={0}>
          {items.map((item, i) => (
            <Fragment key={item.id}>
              {i > 0 && <Divider />}
              <HomeRow
                to={item.to}
                icon={item.icon}
                color={item.color}
                title={item.title}
                detail={[item.what, notificationWhen(item.at)].filter(Boolean).join(" · ")}
              />
            </Fragment>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
